import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, Printer, Sparkles, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';
import { useAdminAuth } from '../context/AdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * PalletLabelsTab — printable Code-39 pallet sticker labels.
 *  * "Existing pallets" list — every pallet currently referenced by a
 *    placement, checkbox-selectable, print via bulk action.
 *  * "Generate new labels" panel — mint N brand-new sequential
 *    PLT-###### IDs and open the printable sheet in a new tab. The
 *    physical sticker is applied to a pallet BEFORE stock is placed.
 */
const PalletLabelsTab = ({ authHeaders }) => {
  const { token } = useAdminAuth();
  const [pallets, setPallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [genCount, setGenCount] = useState(6);
  const [generating, setGenerating] = useState(false);
  const [lastMinted, setLastMinted] = useState([]);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/warehouses/pallets`, { headers: authHeaders });
      setPallets(r.data.pallets || []);
    } catch (e) {
      toast({ title: 'Failed to load pallets', description: e?.response?.data?.detail || e?.message });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const allChecked = pallets.length > 0 && selected.size === pallets.length;
  const someChecked = selected.size > 0 && !allChecked;

  const toggle = (pid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  };
  const toggleAll = () => {
    if (allChecked) { setSelected(new Set()); }
    else { setSelected(new Set(pallets.map((p) => p.pallet_id))); }
  };

  const openSheet = (ids) => {
    if (!ids || ids.length === 0) {
      toast({ title: 'Select at least one pallet', description: 'Tick the checkbox next to the pallets you want to print stickers for.' });
      return;
    }
    const qs = new URLSearchParams({ ids: ids.join(','), token: token || '' });
    window.open(`${API}/admin/warehouses/pallet-stickers?${qs.toString()}`, '_blank', 'noopener,noreferrer');
  };

  const generate = async () => {
    const n = Math.max(1, Math.min(200, Number(genCount) || 1));
    setGenerating(true);
    try {
      const r = await axios.post(
        `${API}/admin/warehouses/pallet-stickers/generate`,
        { count: n },
        { headers: authHeaders },
      );
      const ids = r.data?.pallet_ids || [];
      setLastMinted(ids);
      toast({ title: `Minted ${ids.length} new pallet ID(s)`, description: ids.slice(0, 3).join(', ') + (ids.length > 3 ? '…' : '') });
      openSheet(ids);
    } catch (e) {
      toast({ title: 'Mint failed', description: e?.response?.data?.detail || e?.message });
    } finally {
      setGenerating(false);
    }
  };

  const filteredPallets = useMemo(() => {
    // Show sequential PLT-###### first, then legacy IDs, alpha within each.
    const norm = [...pallets];
    norm.sort((a, b) => {
      const seqA = /^PLT-\d+$/.test(a.pallet_id);
      const seqB = /^PLT-\d+$/.test(b.pallet_id);
      if (seqA && !seqB) return -1;
      if (!seqA && seqB) return 1;
      return a.pallet_id.localeCompare(b.pallet_id);
    });
    return norm;
  }, [pallets]);

  return (
    <div className="space-y-4" data-testid="pallet-labels-tab">
      {/* Generate new labels panel */}
      <div className="bg-gradient-to-br from-emerald-50 to-white rounded-2xl border border-emerald-200 p-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-emerald-500 text-white flex items-center justify-center">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-bold text-emerald-900">Generate new pallet sticker labels</div>
            <div className="text-xs text-emerald-700 mb-3">
              Mints sequential IDs (<span className="font-mono">PLT-000001</span> style). Print → cut → stick on physical pallets BEFORE placing stock. Assigning stock later just references the same ID.
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="text-xs text-emerald-900 font-semibold">Count</label>
              <Input
                type="number"
                min={1}
                max={200}
                value={genCount}
                onChange={(e) => setGenCount(e.target.value)}
                className="w-24 text-sm h-9"
                data-testid="pallet-generate-count"
              />
              <Button
                onClick={generate}
                disabled={generating}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-9 px-4 text-sm"
                data-testid="pallet-generate-btn"
              >
                {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
                Mint & print
              </Button>
              {lastMinted.length > 0 && (
                <div className="text-[11px] text-emerald-700 font-mono ml-1" data-testid="pallet-last-minted">
                  Last minted: {lastMinted.slice(0, 4).join(', ')}{lastMinted.length > 4 ? ` +${lastMinted.length - 4}` : ''}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Existing pallets */}
      <div className="bg-white rounded-xl border border-neutral-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-bold text-neutral-800">Existing pallets ({pallets.length})</div>
            <div className="text-xs text-neutral-500">Reprint stickers for pallets that are already in use.</div>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={load}
              className="text-xs text-neutral-600 hover:text-neutral-900"
              data-testid="pallet-refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
            <Button
              type="button"
              onClick={() => openSheet(Array.from(selected))}
              disabled={selected.size === 0}
              className="bg-[#0078D4] hover:bg-[#005A9E] text-white rounded-full h-9 px-4 text-sm disabled:opacity-40"
              data-testid="pallet-print-selected-btn"
            >
              <Printer className="w-4 h-4 mr-1" /> Print {selected.size ? `(${selected.size})` : 'selected'}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="py-16 flex justify-center text-neutral-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : pallets.length === 0 ? (
          <div className="py-12 text-center text-sm text-neutral-500 italic" data-testid="pallet-labels-empty">
            No pallets in use yet. Use &quot;Mint &amp; print&quot; above to create your first batch, or place stock on a pallet ID from the Placements tab.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pallet-labels-table">
              <thead className="bg-neutral-50 text-[10px] uppercase tracking-wide text-neutral-500">
                <tr>
                  <th className="text-left px-2 py-2 w-8">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      ref={(el) => { if (el) el.indeterminate = someChecked; }}
                      onChange={toggleAll}
                      data-testid="pallet-labels-check-all"
                    />
                  </th>
                  <th className="text-left px-2 py-2">Pallet ID</th>
                  <th className="text-right px-2 py-2">Total qty</th>
                  <th className="text-left px-2 py-2">Products</th>
                  <th className="text-left px-2 py-2">Zone / Location</th>
                  <th className="text-left px-2 py-2 w-24">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredPallets.map((p) => (
                  <tr key={p.pallet_id} className="border-t border-neutral-100" data-testid={`pallet-row-${p.pallet_id}`}>
                    <td className="px-2 py-2">
                      <input
                        type="checkbox"
                        checked={selected.has(p.pallet_id)}
                        onChange={() => toggle(p.pallet_id)}
                        data-testid={`pallet-check-${p.pallet_id}`}
                      />
                    </td>
                    <td className="px-2 py-2 font-mono font-semibold text-[#0F172A]">{p.pallet_id}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{p.qty}</td>
                    <td className="px-2 py-2 text-xs text-neutral-700">
                      {(p.products || []).slice(0, 2).join(', ') || '—'}
                      {(p.products || []).length > 2 && <span className="text-neutral-400"> +{p.products.length - 2}</span>}
                    </td>
                    <td className="px-2 py-2 text-xs text-neutral-500 font-mono">
                      {(p.zones || []).join(', ') || '—'} / {(p.locations || []).join(', ') || '—'}
                    </td>
                    <td className="px-2 py-2">
                      <button
                        type="button"
                        onClick={() => openSheet([p.pallet_id])}
                        className="text-xs text-[#0078D4] hover:underline font-semibold"
                        data-testid={`pallet-print-${p.pallet_id}`}
                      >
                        <Printer className="w-3.5 h-3.5 inline mr-0.5" /> Print
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PalletLabelsTab;
